'use client'

import { useState } from 'react'
import {
  Search,
  Eye,
  Link2,
  ThumbsUp,
  Layers,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRight,
  Sparkles,
  CircleCheck,
  Mail,
  Check,
  Loader2,
} from 'lucide-react'
import {
  LenisFadeIn,
  LenisStaggered,
} from '@/components/animations/LenisReveal'
import OverallScore from './OverallScore'
import ProviderCard from './ProviderCard'
import ScoreCard from './ScoreCard'
import QueryMatrix from './QueryMatrix'
import type { Finding } from './ScoreCard'
import PlatformCTA from '@/components/platform/PlatformCTA'
import { PartialCoverageBanner } from '@/components/tools/result/PartialCoverageBanner'
import {
  responseCoverageForResult,
  type ResponseCoverage,
} from '@/lib/tools/response-coverage'

// Result type matching the API response
interface QueryResult {
  query: string
  queryType: 'brand' | 'competitive' | 'reputation'
  weight: number
  brandMentioned: boolean
  urlCited: boolean
  citedUrls: string[]
  sentiment: 'positive' | 'neutral' | 'negative' | 'not_mentioned'
  prominence: 'primary' | 'mentioned' | 'listed' | 'absent'
  snippet: string
}

interface ProviderResult {
  provider: string
  available: boolean
  queries: QueryResult[]
  expectedQueryCount?: number
  usableQueryCount?: number
  failedQueryCount?: number
  emptyQueryCount?: number
  overallMentionRate: number
  urlCitationRate: number
  reportOnly?: boolean
  error?: string
}

interface ScoreBreakdown {
  category: string
  score: number
  maxScore: number
  details: string
}

interface CitationCheckResult {
  brand: string
  url: string
  category: string
  checkedAt: string
  overallScore: number | null
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null
  scoreStatus?: 'graded' | 'withheld'
  coverage?: ResponseCoverage
  scanCompleteness: number
  providers: ProviderResult[]
  scoreBreakdown: ScoreBreakdown[]
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

interface CitationResultsProps {
  result: CitationCheckResult
  /**
   * 'public' (default) renders the full marketing surface. 'dashboard' hides
   * public-only chrome (the email-share row, `PlatformCTA`, and the gradient
   * services CTA) so the SAME rich report can be reused inside `/platform/app`.
   * Result content (score ring, partial-scan banner, provider grid, AI
   * analysis, score breakdown with `findingsFor`, query matrix, recommendations
   * with blog links) is identical either way.
   */
  variant?: 'public' | 'dashboard'
}

const BLOG_LINKS: Record<string, string> = {
  'Brand Recognition': '/blogs/build-brand-ai-search-engines-cite',
  'Competitive Visibility':
    '/blogs/geo-playbook-get-cited-chatgpt-perplexity-claude',
  'URL Citations': '/blogs/llms-txt-static-vs-dynamic-implementation-guide',
  'Overall Strategy': '/blogs/seo-vs-geo-vs-aeo-guide-2026',
  Consistency: '/blogs/optimized-ai-search-pixelmojo-results',
}

const PRIORITY_ICONS: Record<string, React.ReactNode> = {
  high: <AlertTriangle size={14} style={{ color: '#F90B8A' }} />,
  medium: <AlertCircle size={14} style={{ color: '#FDC304' }} />,
  low: <Info size={14} style={{ color: '#008dcc' }} />,
}

const PRIORITY_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: '#F90B8A15', text: '#F90B8A' },
  medium: { bg: '#FDC30415', text: '#FDC304' },
  low: { bg: '#008dcc15', text: '#008dcc' },
}

const SCORE_ICONS: Record<string, React.ReactNode> = {
  'Brand Recognition': <Search size={18} />,
  'Competitive Visibility': <Eye size={18} />,
  'URL Citations': <Link2 size={18} />,
  Sentiment: <ThumbsUp size={18} />,
  Consistency: <Layers size={18} />,
}

const SCORE_COLORS: Record<string, string> = {
  'Brand Recognition': '#008dcc',
  'Competitive Visibility': '#8b5cf6',
  'URL Citations': '#20808d',
  Sentiment: '#22c55e',
  Consistency: '#FDC304',
}

export default function CitationResults({
  result,
  variant = 'public',
}: CitationResultsProps) {
  // A withheld run carries no number and no grade. Render nothing numeric
  // rather than a placeholder that would read as a real result.
  const hasUsableScore =
    result.scoreStatus !== 'withheld' &&
    result.overallScore !== null &&
    result.grade !== null
  const responseCoverage = responseCoverageForResult(result)

  // --- Email share state ---
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareStatus, setShareStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle')
  const [shareError, setShareError] = useState('')

  async function handleEmailShare() {
    if (
      !shareEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
    ) {
      setShareError('Please enter a valid email address.')
      setShareStatus('error')
      return
    }

    setShareStatus('sending')
    setShareError('')

    try {
      const res = await fetch('/api/tools/citation-check/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          brand: result.brand,
          url: result.url,
          category: result.category,
          overallScore: result.overallScore,
          grade: result.grade,
          scanCompleteness: result.scanCompleteness,
          scoreBreakdown: result.scoreBreakdown,
          recommendations: result.recommendations,
          aiSummary: result.aiSummary?.summary || null,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.message || 'Failed to send email.')
      }

      setShareStatus('sent')
      setTimeout(() => {
        setShareStatus('idle')
        setShowEmailInput(false)
        setShareEmail('')
      }, 2000)
    } catch (err) {
      setShareError(
        err instanceof Error ? err.message : 'Failed to send email.'
      )
      setShareStatus('error')
    }
  }

  // Compute findings for each score category
  const activeProviders = result.providers.filter(p => p.available)

  function findingsFor(category: string): Finding[] {
    const breakdown = result.scoreBreakdown.find(s => s.category === category)
    if (!breakdown) return []

    const findings: Finding[] = []
    const pct =
      breakdown.maxScore > 0
        ? Math.round((breakdown.score / breakdown.maxScore) * 100)
        : 0

    switch (category) {
      case 'Brand Recognition': {
        const brandQueries = activeProviders.flatMap(p =>
          p.queries.filter(q => q.queryType === 'brand')
        )
        const mentioned = brandQueries.filter(q => q.brandMentioned).length
        findings.push({
          text: `${mentioned}/${brandQueries.length} brand queries returned a mention`,
          status: pct >= 50 ? 'good' : 'bad',
        })
        // Per-provider breakdown
        for (const p of activeProviders) {
          const pBrand = p.queries.filter(q => q.queryType === 'brand')
          const pMentioned = pBrand.filter(q => q.brandMentioned).length
          findings.push({
            text: `${p.provider}: ${pMentioned}/${pBrand.length}`,
            status: pMentioned > 0 ? 'good' : 'bad',
          })
        }
        break
      }
      case 'Competitive Visibility': {
        const compQueries = activeProviders.flatMap(p =>
          p.queries.filter(q => q.queryType === 'competitive')
        )
        const mentioned = compQueries.filter(q => q.brandMentioned).length
        findings.push({
          text: `Mentioned in ${mentioned}/${compQueries.length} competitive queries`,
          status: pct >= 30 ? 'good' : 'bad',
        })
        const primary = compQueries.filter(
          q => q.prominence === 'primary'
        ).length
        if (primary > 0) {
          findings.push({
            text: `Primary mention in ${primary} queries`,
            status: 'good',
          })
        }
        break
      }
      case 'URL Citations': {
        const perplexity = activeProviders.find(
          p => p.provider === 'Perplexity'
        )
        if (perplexity) {
          const cited = perplexity.queries.filter(q => q.urlCited).length
          findings.push({
            text: `${cited}/${perplexity.queries.length} Perplexity queries cite your URL`,
            status: cited > 0 ? 'good' : 'bad',
          })
        } else {
          findings.push({
            text: 'Perplexity not available for URL citation check',
            status: 'neutral',
          })
        }
        findings.push({
          text: 'Only Perplexity provides URL-level citations via API',
          status: 'neutral',
        })
        break
      }
      case 'Sentiment': {
        const allMentioned = activeProviders.flatMap(p =>
          p.queries.filter(q => q.brandMentioned)
        )
        if (allMentioned.length === 0) {
          findings.push({
            text: 'No mentions to analyze',
            status: 'bad',
          })
        } else {
          const positive = allMentioned.filter(
            q => q.sentiment === 'positive'
          ).length
          const negative = allMentioned.filter(
            q => q.sentiment === 'negative'
          ).length
          const neutral = allMentioned.filter(
            q => q.sentiment === 'neutral'
          ).length
          if (positive > 0)
            findings.push({
              text: `${positive} positive mention${positive !== 1 ? 's' : ''}`,
              status: 'good',
            })
          if (neutral > 0)
            findings.push({
              text: `${neutral} neutral mention${neutral !== 1 ? 's' : ''}`,
              status: 'neutral',
            })
          if (negative > 0)
            findings.push({
              text: `${negative} negative mention${negative !== 1 ? 's' : ''}`,
              status: 'bad',
            })
        }
        break
      }
      case 'Consistency': {
        const withMention = activeProviders.filter(
          p => p.overallMentionRate > 0
        )
        findings.push({
          text: `${withMention.length}/${activeProviders.length} providers mention your brand`,
          status:
            withMention.length >= activeProviders.length * 0.5 ? 'good' : 'bad',
        })
        for (const p of activeProviders) {
          findings.push({
            text: `${p.provider}: ${Math.round(p.overallMentionRate * 100)}% mention rate`,
            status: p.overallMentionRate > 0 ? 'good' : 'bad',
          })
        }
        break
      }
    }

    return findings
  }

  return (
    <div className='space-y-8'>
      {/* Overall Score */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <div className='text-center mb-2'>
            <p className='text-xs text-muted-foreground'>
              Citation results for
            </p>
            <p className='text-sm font-medium text-foreground'>
              {result.brand}
            </p>
            <p className='text-xs text-muted-foreground truncate max-w-lg mx-auto'>
              {result.url} &middot; {result.category}
            </p>
          </div>
          {hasUsableScore && (
            <OverallScore score={result.overallScore!} grade={result.grade!} />
          )}
          {!hasUsableScore && result.scoreStatus === 'withheld' && (
            <div className='mt-5'>
              {responseCoverage ? (
                <PartialCoverageBanner coverage={responseCoverage} />
              ) : (
                <div
                  className='border px-4 py-3 text-sm text-foreground/80 leading-relaxed text-center'
                  style={{
                    backgroundColor:
                      'color-mix(in srgb, var(--warning-text) 6%, transparent)',
                    borderColor:
                      'color-mix(in srgb, var(--warning-text) 12%, transparent)',
                  }}
                >
                  <strong style={{ color: 'var(--warning-text)' }}>
                    Score withheld:
                  </strong>{' '}
                  the evaluation did not return enough verified responses to
                  grade this run.
                </div>
              )}
            </div>
          )}
          {/* Before response-level withholding existed, historical reports
              could contain a numeric partial score and scanCompleteness meant
              provider coverage. Preserve that legacy disclosure only when the
              result has no scoreStatus contract. */}
          {result.scoreStatus === undefined &&
            hasUsableScore &&
            result.scanCompleteness < 100 && (
              <div
                className='mt-3 border px-4 py-2.5 text-xs text-foreground/80 leading-relaxed text-center'
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--warning-text) 6%, transparent)',
                  borderColor:
                    'color-mix(in srgb, var(--warning-text) 12%, transparent)',
                }}
              >
                <strong style={{ color: 'var(--warning-text)' }}>
                  Partial scan:
                </strong>{' '}
                Only {result.scanCompleteness}% of AI providers were available.
                Score reflects only providers we could query.
              </div>
            )}
        </div>
      </LenisFadeIn>

      {/* Email share — public surface only, and only when there is a score to
          share. The share payload carries overallScore/grade, so a withheld run
          would post nulls the endpoint rejects. */}
      {variant !== 'dashboard' && hasUsableScore && (
        <div className='flex flex-col items-center gap-2'>
          {!showEmailInput ? (
            <button
              onClick={() => setShowEmailInput(true)}
              className='inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors'
            >
              <Mail size={16} />
              Email results
            </button>
          ) : (
            <div className='flex items-center gap-2 w-full max-w-md'>
              <input
                type='email'
                value={shareEmail}
                onChange={e => {
                  setShareEmail(e.target.value)
                  if (shareStatus === 'error') setShareStatus('idle')
                }}
                placeholder='colleague@company.com'
                className='flex-1 h-10 px-4 text-sm rounded-full border border-border/60 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40'
                onKeyDown={e => {
                  if (e.key === 'Enter') handleEmailShare()
                }}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                autoFocus
              />
              <button
                onClick={handleEmailShare}
                disabled={shareStatus === 'sending' || shareStatus === 'sent'}
                className='inline-flex items-center gap-2 text-sm font-medium h-10 px-5 rounded-full border border-border/60 bg-background text-foreground/80 hover:text-foreground transition-colors disabled:opacity-50'
              >
                {shareStatus === 'sending' ? (
                  <Loader2 size={16} className='animate-spin' />
                ) : shareStatus === 'sent' ? (
                  <>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ color: '#22c55e' }}>Sent!</span>
                  </>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          )}
          {shareStatus === 'error' && shareError && (
            <p className='text-xs' style={{ color: '#F90B8A' }}>
              {shareError}
            </p>
          )}
        </div>
      )}

      {/* Provider Grid */}
      <LenisFadeIn>
        <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
          <h2 className='text-lg font-semibold text-foreground mb-4'>
            Provider Results
          </h2>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            {result.providers.map(p => (
              <ProviderCard key={p.provider} provider={p} />
            ))}
          </div>
        </div>
      </LenisFadeIn>

      {variant !== 'dashboard' && (
        <PlatformCTA
          currentTool='Citations'
          score={hasUsableScore ? result.overallScore! : undefined}
          grade={hasUsableScore ? result.grade! : undefined}
        />
      )}

      {/* AI Analysis */}
      {result.aiSummary && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
              <Sparkles size={20} style={{ color: '#8b5cf6' }} />
              AI Analysis
            </h2>

            <p className='text-sm text-foreground/90 leading-relaxed mb-4'>
              {result.aiSummary.summary}
            </p>

            {result.aiSummary.actions.length > 0 && (
              <div className='space-y-2 mb-4'>
                <h3 className='text-xs font-semibold text-muted-foreground uppercase tracking-wide'>
                  Priority Actions
                </h3>
                <ul className='space-y-2'>
                  {result.aiSummary.actions.map((action, i) => (
                    <li
                      key={i}
                      className='flex items-start gap-2.5 text-sm text-foreground/80'
                    >
                      <CircleCheck
                        size={16}
                        className='mt-0.5 shrink-0'
                        style={{ color: '#008dcc' }}
                      />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div
              className='rounded-lg p-3 text-xs text-foreground/70 leading-relaxed'
              style={{
                backgroundColor: '#8b5cf610',
                border: '1px solid #8b5cf620',
              }}
            >
              {result.aiSummary.context}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* Score Breakdown — the paragraph explains a scoring model that a
          withheld run has no cards for, so the block is gated as a whole. */}
      {hasUsableScore && (
        <>
          <p className='text-xs text-muted-foreground mb-3'>
            Most signals measure whether AI answers mention your brand; only URL
            Citations confirm a linked source.
          </p>
          <LenisStaggered>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {result.scoreBreakdown.map(s => (
                <ScoreCard
                  key={s.category}
                  title={s.category}
                  icon={SCORE_ICONS[s.category] || <Search size={18} />}
                  score={s.score}
                  maxScore={s.maxScore}
                  findings={findingsFor(s.category)}
                  color={SCORE_COLORS[s.category] || 'var(--primary)'}
                />
              ))}
            </div>
          </LenisStaggered>
        </>
      )}

      {/* Query Matrix — aligns providers by array index. A withheld run may be
          missing a query row (failed/blank responses are excluded by design),
          which would shift later responses under the wrong prompt. */}
      {hasUsableScore && (
        <LenisFadeIn>
          <QueryMatrix providers={result.providers} />
        </LenisFadeIn>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <LenisFadeIn>
          <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
            <h2 className='text-lg font-semibold text-foreground mb-4'>
              Recommendations
            </h2>
            <div className='space-y-3'>
              {result.recommendations.map((rec, i) => {
                const pc = PRIORITY_COLORS[rec.priority]
                const blogLink = BLOG_LINKS[rec.category]
                return (
                  <div
                    key={i}
                    className='flex items-start gap-3 bg-background border border-border/60 rounded-xl p-4'
                  >
                    <div className='mt-0.5'>{PRIORITY_ICONS[rec.priority]}</div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center gap-2 mb-1'>
                        <span
                          className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5'
                          style={{ backgroundColor: pc.bg, color: pc.text }}
                        >
                          {rec.priority}
                        </span>
                        <span className='text-[11px] text-muted-foreground'>
                          {rec.category}
                        </span>
                      </div>
                      <p className='text-sm text-foreground/80'>
                        {rec.message}
                      </p>
                      {blogLink && (
                        <a
                          href={blogLink}
                          className='inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1.5'
                        >
                          Learn more <ArrowRight size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </LenisFadeIn>
      )}

      {/* CTA — public surface only */}
      {variant !== 'dashboard' && (
        <LenisFadeIn>
          <div
            className='relative overflow-hidden rounded-2xl p-8 md:p-10 text-center'
            style={{
              background: 'linear-gradient(135deg, #8c1b4c, #b32a6c, #e54b8c)',
            }}
          >
            <h3 className='text-xl md:text-2xl font-bold text-white mb-3'>
              Want to improve your AI citation presence?
            </h3>
            <p
              className='text-sm mb-6 max-w-lg mx-auto'
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              Our AI visibility audit covers knowledge graph mapping, entity
              resolution, citation tracking, and a custom optimization roadmap.
            </p>
            <div className='flex flex-wrap items-center justify-center gap-3'>
              <a
                href='/services/ai-visibility-strategy'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full border-2 border-white text-white font-medium text-sm transition-all duration-300'
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = 'white'
                  e.currentTarget.style.color = '#b32a6c'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = 'white'
                }}
              >
                Get a Strategy Session
              </a>
              <a
                href='/platform'
                className='inline-flex items-center justify-center h-11 px-6 rounded-full text-sm font-medium transition-colors'
                style={{ color: 'rgba(255,255,255,0.9)' }}
              >
                Or DIY with Radar (free){' '}
                <ArrowRight size={16} className='ml-1' />
              </a>
            </div>
          </div>
        </LenisFadeIn>
      )}
    </div>
  )
}
